import { Get, Patch, Post, uploadMedia } from "@/axios/AxiosFunctions";
import Box from "@/components/Box";
import Breadcrumb from "@/components/Breadcrumb";
import CmsLoader from "@/components/CmsLoader";
import { Button } from "@/components/Core/Button";
import { DropDown } from "@/components/Core/DropDown";
import { Input } from "@/components/Core/Input";
import MultiFileUpload from "@/components/Core/MultiFileUpload";
import SideBarSkeleton from "@/components/Core/SideBarSkeleton";
import { TextArea } from "@/components/Core/TextArea";
import { BaseURL } from "@/config/apiUrl";
import { allowedImageTypes } from "@/constant/constants";
import {
  apiHeader,
  RenderToast,
  validateNestedParams,
} from "@/helper/HelperFunction";
import { useCallback, useEffect, useState } from "react";
import { MdCancel } from "react-icons/md";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import classes from "./AddEditInventory.module.css";
import WarehousesByState from "./WarehousesByState";
import AttachmentUpload from "@/components/Core/AttachmentUpload";
const InventoryDetailsLoadingStates = {
  GET_INVENTORY_DETAILS: "GET_INVENTORY_DETAILS",
  GET_BRANDS_AND_CATEGORIES: "GET_BRANDS_AND_CATEGORIES",
  UPDATE_INVENTORY: "UPDATE_INVENTORY",
};
const AddEditInventory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { access_token } = useSelector((state) => state.authReducer);
  const { states: allStates } = useSelector((state) => state.commonReducer);
  const [brand, setBrand] = useState(null);
  const [mpn, setMpn] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [itemDetails, setItemDetails] = useState("");
  const [itemCategory, setItemCategory] = useState(null);
  const [itemTags, setItemTags] = useState([]);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [length, setLength] = useState("");
  const [weight, setWeight] = useState("");
  const [compatibility, setCompatibility] = useState("");
  const [foamDistributorsCost, setFoamDistributorsCost] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [subsidiary, setSubsidiary] = useState("");
  const [states, setStates] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachment, setAttachment] = useState(null);
  // add edit inventory
  const handleSubmit = async () => {
    // handle states
    const allStateObj = allStates?.find(
      (state) => state?.name?.toLowerCase() === "all"
    );
    const isAllSelected = states?.includes(allStateObj?._id);
    const finalStates = isAllSelected
      ? allStates?.map((state) => state?._id)
      : states;

    let params = {
      ...(id && { productId: id }),
      displayName,
      itemCategory,
      brand,
      unitPrice: unitPrice ? Number(unitPrice) : null,
      images,
      states: finalStates,
    };
    if (!validateNestedParams(params)) {
      return;
    }
    params = {
      ...params,
      mpn,
      width: width ? Number(width) : null,
      height: height ? Number(height) : null,
      length: length ? Number(length) : null,
      weight: weight ? Number(weight) : null,
      compatibility,
      foamDistributorsCost: foamDistributorsCost
        ? Number(foamDistributorsCost)
        : null,
      subsidiary,
      itemTags,
      description,
      itemDetails,
      attachment: attachment,
    };
    if (discountPercentage) {
      params.discountPercentage = Number(discountPercentage);
    }
    params.warehouses = warehouses;
    const apiUrl = BaseURL(`products`);
    setIsLoading(InventoryDetailsLoadingStates.UPDATE_INVENTORY);
    const response = id
      ? await Patch(apiUrl, params, apiHeader(access_token))
      : await Post(apiUrl, params, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: id
          ? "Inventory updated successfully"
          : "Inventory added successfully",
      });
      navigate("/inventory-management");
    }
    setIsLoading(null);
  };
  //   get inventory details
  const getInventoryDetails = useCallback(async () => {
    const brandsUrl = BaseURL("brands/admin/all");
    const categoriesUrl = BaseURL("categories/admin/all");
    const warehousesUrl = BaseURL("warehouses/admin/warehouses/all");
    const productUrl = BaseURL(`products/detail/${id}`);
    setIsLoading(InventoryDetailsLoadingStates.GET_BRANDS_AND_CATEGORIES);
    const [
      brandsResponse,
      categoriesResponse,
      warehousesResponse,
      productResponse,
    ] = await Promise.allSettled([
      Get(brandsUrl, access_token),
      Get(categoriesUrl, access_token),
      Get(warehousesUrl, access_token),
      id ? Get(productUrl, access_token) : null,
    ]);
    if (brandsResponse?.value) {
      setBrandOptions(brandsResponse?.value?.data?.data?.brands);
    }
    if (categoriesResponse?.value) {
      setCategoryOptions(categoriesResponse?.value?.data?.data?.categories);
    }
    if (warehousesResponse?.value) {
      setWarehouseOptions(warehousesResponse?.value?.data?.data?.warehouses);
    }
    if (productResponse?.value) {
      const { data: productData } = productResponse?.value;
      setDisplayName(productData?.displayName);
      setItemCategory(productData?.itemCategory?._id);
      setBrand(productData?.brand?._id);
      setMpn(productData?.mpn);
      setWidth(productData?.width);
      setHeight(productData?.height);
      setLength(productData?.length);
      setWeight(productData?.weight);
      setUnitPrice(productData?.unitPrice);
      setDiscountPercentage(productData?.discountPercentage);
      setCompatibility(productData?.compatibility);
      setFoamDistributorsCost(productData?.foamDistributorsCost);
      setSubsidiary(productData?.subsidiary);
      setItemTags(productData?.itemTags);
      setItemDetails(productData?.itemDetails);
      setDescription(productData?.description);
      setStates(productData?.states?.map((state) => state?._id));
      setWarehouses(
        productData?.warehouses?.map((item) => ({
          ...item,
          warehouseId: item?.warehouseId?._id,
          state: item?.state?._id,
        }))
      );
      setImages(productData?.images);
      setAttachment(productData?.attachment);
      
    }
    setIsLoading(null);
  }, [access_token, id]);

  useEffect(() => {
    getInventoryDetails();
  }, [getInventoryDetails]);

  // upload images
  const uploadImages = async (uploadedFiles) => {
    const filteredFiles = uploadedFiles?.filter((file) => file instanceof File);
    if (filteredFiles?.length === 0) return;
    setUploading(true);
    const response = await uploadMedia(filteredFiles);
    if (response) {
      setImages([...images, ...response]);
    } else {
      RenderToast({
        type: "error",
        message: "Failed to upload images",
      });
      setImages((prev) => prev.filter((file) => typeof file === "string"));
    }
    setUploading(false);
  };

  // upload attachments
  const uploadAttachment = async (uploadedFile) => {
    setAttachment((prev) => uploadedFile);

    if (!(uploadedFile instanceof File)) return;
    setUploadingAttachments(true);
    const response = await uploadMedia([uploadedFile]);
    if (response) {
      setAttachment((prev) => response?.[0]);
    } else {
      RenderToast({
        type: "error",
        message: "Failed to upload attachments",
      });
    }
    setUploadingAttachments(false);
  };
  console.log(attachment, "attachment");
  return (
    <>
      <SideBarSkeleton>
        <div className={classes.mainWrapper}>
          <div className={classes.headerTitle}>
            <h3>{id ? "Edit" : "Add"} Inventory</h3>
            <Breadcrumb />
          </div>
          {isLoading === InventoryDetailsLoadingStates.GET_INVENTORY_DETAILS ||
          isLoading ===
            InventoryDetailsLoadingStates.GET_BRANDS_AND_CATEGORIES ? (
            <Box>
              <CmsLoader
                loaders={[
                  [
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                    "input",
                  ],
                  "input",
                  "textarea",
                ]}
              />
            </Box>
          ) : (
            <Box>
              <div className={classes.gridWrapper}>
                <Input
                  label="Display Name"
                  placeholder="Enter Display Name"
                  value={displayName}
                  setter={setDisplayName}
                />
                <DropDown
                  options={categoryOptions}
                  label="Item Category"
                  placeholder="Select Item Category"
                  value={categoryOptions?.find(
                    (option) => option?._id === itemCategory
                  )}
                  setter={(e) => setItemCategory(e?._id)}
                  optionLabel={"name"}
                  optionValue={"_id"}
                />
                <DropDown
                  options={brandOptions}
                  label="Brand"
                  placeholder="Select Brand"
                  value={brandOptions?.find((option) => option?._id === brand)}
                  setter={(e) => setBrand(e?._id)}
                  optionLabel={"name"}
                  optionValue={"_id"}
                />
                <Input
                  label="MPN"
                  placeholder="Enter MPN"
                  value={mpn}
                  setter={setMpn}
                />
                <Input
                  label="Width (in)"
                  placeholder="Enter Width"
                  value={width}
                  setter={setWidth}
                  regexType={"point"}
                />
                <Input
                  label="Height (in)"
                  placeholder="Enter Height"
                  value={height}
                  setter={setHeight}
                  regexType={"point"}
                />
                <Input
                  label="Length (in)"
                  placeholder="Enter Length"
                  value={length}
                  setter={setLength}
                  regexType={"point"}
                />
                <Input
                  label="Weight (lbs)"
                  placeholder="Enter Weight"
                  value={weight}
                  setter={setWeight}
                  regexType={"point"}
                />
                <Input
                  label="Unit Price"
                  placeholder="Enter Unit Price"
                  value={unitPrice}
                  setter={setUnitPrice}
                  regexType={"point"}
                />
                <Input
                  label="Discount (%)"
                  placeholder="Enter Discount (%)"
                  value={discountPercentage}
                  setter={(e) => {
                    if (e && Number(e) > 100) {
                      return RenderToast({
                        type: "error",
                        message: "Discount cannot be greater than 100",
                      });
                    }
                    setDiscountPercentage(e);
                  }}
                  regexType="point"
                />
                <Input
                  label="Compatibility"
                  placeholder="Enter Compatibility"
                  value={compatibility}
                  setter={setCompatibility}
                />
                <Input
                  label="Foam Distributors Cost"
                  placeholder="Enter Foam Distributors Cost"
                  value={foamDistributorsCost}
                  setter={setFoamDistributorsCost}
                  regexType={"point"}
                />
                <Input
                  label="Subsidiary"
                  placeholder="Enter Subsidiary"
                  value={subsidiary}
                  setter={setSubsidiary}
                />
                <div className={classes.fullGrid}>
                  <TagsInput array={itemTags} setArray={setItemTags} />
                </div>
                <div className={classes.fullGrid}>
                  <TextArea
                    label="Description"
                    placeholder="Enter Description"
                    value={description}
                    setter={setDescription}
                  />
                </div>
                <div className={classes.fullGrid}>
                  <TextArea
                    label="Item Details"
                    placeholder="Enter Item Details"
                    value={itemDetails}
                    setter={setItemDetails}
                  />
                </div>
                <div className={classes.fullGrid}>
                  <MultiFileUpload
                    label="Images"
                    files={images}
                    setFiles={(e) => {
                      setImages(e);
                      uploadImages(e);
                    }}
                    onDelete={(index) => {
                      setImages((prev) => prev.filter((_, i) => i !== index));
                    }}
                    uploading={uploading}
                    acceptTypes={allowedImageTypes}
                    text="Upload Images"
                    maxFiles={10}
                    allowDrag={true}
                  />
                </div>
                <div className={classes.fullGrid}>
                  <AttachmentUpload
                    label="Attachment"
                    state={attachment}
                    setter={(e) => {
                      uploadAttachment(e);
                    }}
                    onDelete={() => setAttachment(null)}
                    loading={uploadingAttachments}
                    acceptedTypes="pdf"
                  />
                </div>
                <div className={classes.fullGrid}>
                  <WarehousesByState
                    allStates={allStates}
                    selectedStateIds={states}
                    setSelectedStateIds={setStates}
                    warehouseOptions={warehouseOptions}
                    warehouses={warehouses}
                    setWarehouses={setWarehouses}
                    isEdit={!!id}
                  />
                </div>
              </div>
              <div className={classes.buttonsContainer}>
                <Button
                  label={
                    isLoading === InventoryDetailsLoadingStates.UPDATE_INVENTORY
                      ? "Saving..."
                      : id
                      ? "Update"
                      : "Save"
                  }
                  onClick={handleSubmit}
                  disabled={isLoading || uploading || uploadingAttachments}
                />
                <Button
                  label="Cancel"
                  onClick={() => navigate("/inventory-management")}
                  variant="bordered"
                  disabled={isLoading || uploading || uploadingAttachments}
                />
              </div>
            </Box>
          )}
        </div>
      </SideBarSkeleton>
    </>
  );
};

export default AddEditInventory;

const TagsInput = ({ array, setArray }) => {
  const [text, setText] = useState("");
  const handleAdd = () => {
    if (text.trim() === "") return;
    setArray([...array, text]);
    setText("");
  };

  const handleRemove = (index) => {
    const newArray = [...array];
    newArray.splice(index, 1);
    setArray(newArray);
  };
  return (
    <div className={classes.tagsInput}>
      <Input
        placeholder="Enter Tags"
        value={text}
        setter={setText}
        label="Item Tags"
        onEnter={handleAdd}
      />
      <span className={classes.addTag} onClick={handleAdd}>
        + Add more
      </span>
      <div className={classes.tagsList}>
        {array?.map((tag, index) => (
          <div key={index} className={classes.tag}>
            {tag}
            <MdCancel
              onClick={() => handleRemove(index)}
              size={22}
              color={"#ff0000"}
              className={classes.deleteTag}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
